/**
 * exportTableData — Export tabular data to CSV (Excel-compatible) and simple printable PDF
 */

/** Generate CSV from array of objects and trigger download */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  columns: { key: string; label: string }[],
  filename: string,
): void {
  if (data.length === 0) return;

  const BOM = '\uFEFF'; // UTF-8 BOM for Excel compatibility
  const header = columns.map(c => `"${c.label}"`).join(';');
  const rows = data.map(row =>
    columns.map(c => {
      const val = row[c.key];
      const str = val == null ? '' : String(val);
      return `"${str.replace(/"/g, '""')}"`;
    }).join(';'),
  );

  const csv = BOM + [header, ...rows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${filename}.csv`);
}

/** Generate a simple printable HTML and trigger print dialog (PDF) */
export function exportToPrintPDF<T extends Record<string, unknown>>(
  data: T[],
  columns: { key: string; label: string }[],
  title: string,
  subtitle?: string,
): void {
  const rows = data.map(row =>
    `<tr>${columns.map(c => `<td style="padding:4px 8px;border:1px solid #ddd;font-size:11px">${row[c.key] ?? ''}</td>`).join('')}</tr>`
  ).join('');

  const html = `<!DOCTYPE html><html><head><title>${title}</title>
<style>
  @page { size: A4; margin: 15mm; }
  body { font-family: 'Inter', -apple-system, sans-serif; margin: 0; color: #1e293b; background: #fff; line-height: 1.5; }
  .header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 24px; }
  .header-content h1 { font-size: 20px; font-weight: 800; margin: 0; color: #0f172a; text-transform: uppercase; letter-spacing: 0.05em; }
  .header-content p { font-size: 11px; font-weight: 600; color: #64748b; margin: 4px 0 0 0; text-transform: uppercase; letter-spacing: 0.05em; }
  .header .meta { text-align: right; }
  .header .meta p { font-size: 10px; color: #94a3b8; font-weight: 500; margin: 0; }
  .header .meta .logo-placeholder { font-size: 14px; font-weight: 900; color: #2563eb; letter-spacing: -0.5px; }
  table { border-collapse: collapse; width: 100%; border: 1px solid #e2e8f0; }
  th { background: #f8fafc; padding: 10px 12px; border: 1px solid #e2e8f0; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; text-align: left; }
  td { padding: 8px 12px; border: 1px solid #e2e8f0; font-size: 11px; color: #334155; }
  tbody tr:nth-child(even) { background: #fdfdfd; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; text-align: center; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
</style>
</head><body>
<div class="header">
  <div class="header-content">
    <h1>${title}</h1>
    ${subtitle ? `<p>${subtitle}</p>` : ''}
  </div>
  <div class="meta">
    <div class="logo-placeholder">WORKFLOW</div>
    <p>Exportado em ${new Date().toLocaleString('pt-BR')}</p>
  </div>
</div>
<table><thead><tr>${columns.map(c => `<th>${c.label}</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table>
<div class="footer">Gerado por Workflow Manager &copy; ${new Date().getFullYear()}</div>
</body></html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
